/** @jsxImportSource @emotion/react */

import {
  ChangeEvent,
  ClipboardEvent as ReactClipboardEvent,
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import CircularProgress from '@mui/material/CircularProgress';

import CountdownTimer from '../commons/CountdownTimer';
import PtMetricsLogoIcon from '../../assets/ptMetricsLogo.svg';
import { fonts } from '../../style/fonts';
import { colors } from '../../style/colors';

// Code lifetime mirrors the Lambda's MFA_CODE_TTL_SECONDS (300 = 5:00). Display
// only; the server is authoritative on expiry.
const CODE_TTL_SECONDS = 300;
// Cooldown before "Resend" is allowed again (anti email-bomb on the client; the
// Lambda also rate-limits server-side).
const RESEND_COOLDOWN_SECONDS = 20;
const CODE_LENGTH = 6;
const CODE_PATTERN = /^\d{6}$/;

/**
 * Status of the surrounding sign-in flow, owned by the caller (AppWithAuth).
 * - 'idle'      : waiting for the user to type the code.
 * - 'verifying' : a code submission is in flight (sendCustomChallengeAnswer).
 * - 'resending' : a resend request is in flight.
 */
export type MfaPanelStatus = 'idle' | 'verifying' | 'resending';

export interface MfaInlinePanelProps {
  // The address the code was emailed to (shown in guidance copy).
  email: string;
  // Submit the entered 6-digit code. Caller drives sendCustomChallengeAnswer.
  onSubmit: (code: string) => void;
  // Request a fresh code (RESEND_CODE path). Caller drives the resend.
  onResend: () => void;
  // User abandoned MFA ("Back to sign in") — caller rejects the sign-in flow.
  onCancel: () => void;
  status: MfaPanelStatus;
  // Optional inline error (e.g. "Incorrect code") surfaced under the bar. The
  // caller only ever sends CODE-oriented copy here — never a password message.
  errorText?: string;
}

const screenCss = {
  // Full-screen replacement for the sign-in card (NOT a dialog/popup). The
  // Authenticator is hidden underneath while this is mounted, so this opaque
  // surface becomes the sign-in screen with the code bar in place of the
  // email/password form.
  position: 'fixed' as const,
  inset: 0,
  zIndex: 1300,
  background: colors.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  boxSizing: 'border-box' as const,
};

const cardCss = {
  width: '100%',
  maxWidth: 460,
  boxSizing: 'border-box' as const,
  background: colors.white,
  boxShadow: '4px 4px 10px 0px #00000040',
  border: `1px solid ${colors.dividerGrey}`,
  borderRadius: 10,
  padding: '40px 32px',
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  gap: 16,
};

const boxCss = (error: boolean, filled: boolean) => ({
  width: 44,
  height: 56,
  textAlign: 'center' as const,
  fontFamily: '"IntelOne Text", sans-serif',
  fontSize: 24,
  fontWeight: 500,
  color: colors.black,
  background: colors.white,
  border: `1px solid ${
    error ? colors.red : filled ? colors.blue3 : colors.dividerGrey
  }`,
  borderRadius: 8,
  outline: 'none',
  transition: 'border-color 0.15s, box-shadow 0.15s',
  '&:focus': {
    borderColor: error ? colors.red : colors.blue3,
    boxShadow: `0 0 0 2px ${
      error ? 'rgba(255,78,78,0.25)' : 'rgba(0,104,181,0.25)'
    }`,
  },
  // Shrink so six boxes + gaps still fit inside the card on narrow windows.
  '@media (max-width: 430px)': {
    width: 38,
    height: 50,
    fontSize: 20,
  },
});

const primaryBtnCss = (disabled: boolean) => ({
  width: '100%',
  height: 44,
  borderRadius: 8,
  border: 'none',
  cursor: disabled ? 'not-allowed' : 'pointer',
  color: disabled ? '#a9a9a9' : colors.white,
  fontFamily: '"IntelOne Text", sans-serif',
  fontSize: 16,
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Matches the Authenticator's primary button gradient (see signInCss).
  background: disabled
    ? '#d3d3d3'
    : 'linear-gradient(338.15deg, #004a86 12.01%, #0068b5 83.38%)',
});

const linkBtnCss = (disabled: boolean) => ({
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: disabled ? 'default' : 'pointer',
  color: disabled ? colors.darkGrey : colors.blue3,
  fontFamily: '"IntelOne Text", sans-serif',
  fontSize: 14,
  fontWeight: 500,
  textDecoration: disabled ? 'none' : 'underline',
});

export default function MfaInlinePanel({
  email,
  onSubmit,
  onResend,
  onCancel,
  status,
  errorText,
}: MfaInlinePanelProps) {
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [expired, setExpired] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(true);
  // Inline status line (e.g. "A new code has been sent."). Kept LOCAL — this
  // panel renders in the pre-auth Authenticator shell, which is OUTSIDE the
  // Redux <Provider>, so useDispatch/notify here would crash the whole app
  // (TypeError: Cannot destructure property 'store' ... null).
  const [notice, setNotice] = useState<string | undefined>(undefined);
  // Suppress the red error state as soon as the user starts re-typing.
  const [errorVisible, setErrorVisible] = useState(false);
  // Remounting the countdowns by bumping a key restarts them after a resend.
  const [timerKey, setTimerKey] = useState(0);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  // Guards the auto-submit so a completed code fires onSubmit exactly once.
  const submittedRef = useRef(false);
  // Tracks the previous status so we can detect a failed verify (verifying ->
  // idle while still mounted; a successful verify unmounts the panel).
  const prevStatusRef = useRef<MfaPanelStatus>(status);

  const busy = status === 'verifying' || status === 'resending';
  const code = digits.join('');
  const codeValid = CODE_PATTERN.test(code);
  const canSubmit = codeValid && !busy && !expired;

  const focusBox = (index: number) => {
    const idx = Math.max(0, Math.min(CODE_LENGTH - 1, index));
    window.requestAnimationFrame(() => {
      const el = inputRefs.current[idx];
      // Skip while the box is disabled (mid-verify) — focus() is a no-op on a
      // disabled element and would silently drop focus to <body>.
      if (el && !el.disabled) {
        el.focus();
        el.select?.();
      }
    });
  };

  // Focus the first box on mount.
  useEffect(() => {
    focusBox(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Surface the red error state whenever the caller sends a new error.
  useEffect(() => {
    setErrorVisible(Boolean(errorText));
  }, [errorText]);

  // After a FAILED verify (status went verifying -> idle while still mounted),
  // clear the bar so the user re-enters a fresh code. Do NOT reset
  // submittedRef here: at this commit `digits` is still full (the clear is a
  // pending state update), so re-arming now would let the auto-submit effect
  // immediately re-fire the same wrong code. The auto-submit effect re-arms the
  // guard on its own once `code` becomes incomplete on the next render.
  useEffect(() => {
    if (prevStatusRef.current === 'verifying' && status === 'idle') {
      setDigits(Array(CODE_LENGTH).fill(''));
      focusBox(0);
    }
    prevStatusRef.current = status;
  }, [status]);

  // Auto-submit once all six boxes are filled (standard OTP-bar behaviour).
  useEffect(() => {
    if (code.length < CODE_LENGTH) {
      submittedRef.current = false;
      return;
    }
    if (codeValid && !busy && !expired && !submittedRef.current) {
      submittedRef.current = true;
      onSubmit(code);
    }
  }, [code, codeValid, busy, expired, onSubmit]);

  // Single submission funnel. The auto-submit effect (above), the Enter key, and
  // the Verify button ALL route a submit through this one guard, so submittedRef
  // is the sole gate. Without it, a user who fills the 6th box (auto-submit
  // fires, guard set) and then immediately presses Enter / clicks Verify — before
  // the caller flips status to 'verifying' — would submit the SAME code twice,
  // burning an MFA attempt or tripping the lockout on a duplicate answer.
  const submitCode = () => {
    if (!codeValid || busy || expired || submittedRef.current) {
      return;
    }
    submittedRef.current = true;
    onSubmit(code);
  };

  const fillFrom = (start: number, raw: string) => {
    const chars = raw.replace(/\D/g, '').slice(0, CODE_LENGTH - start);
    if (!chars) {
      return;
    }
    setDigits((prev) => {
      const next = [...prev];
      for (let k = 0; k < chars.length; k++) {
        next[start + k] = chars.charAt(k);
      }
      return next;
    });
    focusBox(start + chars.length);
  };

  const handleDigitChange =
    (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(/\D/g, '');
      setErrorVisible(false);
      if (raw === '') {
        setDigits((prev) => {
          const next = [...prev];
          next[index] = '';
          return next;
        });
        return;
      }
      if (raw.length === 1) {
        setDigits((prev) => {
          const next = [...prev];
          next[index] = raw;
          return next;
        });
        focusBox(index + 1);
        return;
      }
      // More than one digit landed in a single box (mobile autofill / paste).
      fillFrom(index, raw);
    };

  const handleKeyDown =
    (index: number) => (e: ReactKeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        submitCode();
        return;
      }
      if (e.key === 'Backspace') {
        setErrorVisible(false);
        if (digits[index]) {
          setDigits((prev) => {
            const next = [...prev];
            next[index] = '';
            return next;
          });
        } else if (index > 0) {
          setDigits((prev) => {
            const next = [...prev];
            next[index - 1] = '';
            return next;
          });
          focusBox(index - 1);
        }
        e.preventDefault();
        return;
      }
      if (e.key === 'ArrowLeft' && index > 0) {
        focusBox(index - 1);
      } else if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
        focusBox(index + 1);
      }
    };

  const handlePaste = (e: ReactClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    setErrorVisible(false);
    fillFrom(0, e.clipboardData.getData('text'));
  };

  const handleResend = () => {
    if (resendDisabled || busy) {
      return;
    }
    onResend();
    // Restart the expiry + cooldown timers, clear the bar, re-arm auto-submit.
    setDigits(Array(CODE_LENGTH).fill(''));
    setExpired(false);
    setResendDisabled(true);
    setErrorVisible(false);
    submittedRef.current = false;
    setTimerKey((k) => k + 1);
    setNotice('A new code has been sent. Check your inbox and spam/junk.');
    focusBox(0);
  };

  const handleExpiry = () => {
    setExpired(true);
    setNotice(undefined);
  };

  const handleCooldownEnd = () => {
    setResendDisabled(false);
  };

  const showError = errorVisible && Boolean(errorText);

  return (
    <div css={screenCss}>
      <div css={cardCss} role="region" aria-labelledby="mfa-inline-title">
        <PtMetricsLogoIcon width={253} height={70} />
        <div id="mfa-inline-title" style={fonts.modalHeader as CSSProperties}>
          Enter verification code
        </div>
        <div
          css={{
            ...(fonts.text as object),
            color: colors.darkerGrey,
            textAlign: 'center',
          }}
        >
          We emailed a 6-digit code to{' '}
          <strong css={{ color: colors.black }}>{email}</strong>. Check your
          inbox — and your spam/junk folder if you don&apos;t see it.
        </div>

        <div
          css={{
            display: 'flex',
            gap: 8,
            justifyContent: 'center',
            '@media (max-width: 430px)': { gap: 6 },
          }}
          onPaste={handlePaste}
        >
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={(el) => {
                inputRefs.current[i] = el;
              }}
              value={digit}
              onChange={handleDigitChange(i)}
              onKeyDown={handleKeyDown(i)}
              onFocus={(e) => e.target.select()}
              disabled={status === 'verifying'}
              type="text"
              inputMode="numeric"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              maxLength={1}
              aria-label={`digit ${i + 1}`}
              aria-invalid={showError}
              css={boxCss(showError, digit !== '')}
            />
          ))}
        </div>

        {/* Reserve a row for error/notice so the layout doesn't jump. The live
            region announces wrong-code / resend feedback to screen readers
            (assertive for errors, polite for the resend notice). */}
        <div
          css={{ minHeight: 18, textAlign: 'center' }}
          role={showError ? 'alert' : 'status'}
          aria-live={showError ? 'assertive' : 'polite'}
        >
          {showError ? (
            <span css={{ ...(fonts.text as object), color: colors.red }}>
              {errorText}
            </span>
          ) : notice ? (
            <span css={{ ...(fonts.text as object), color: colors.green }}>
              {notice}
            </span>
          ) : null}
        </div>

        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            minHeight: 24,
          }}
        >
          <span
            css={{
              ...(fonts.mini as object),
              color: colors.darkerGrey,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {expired ? (
              'Code expired'
            ) : (
              <>
                Code expires in
                <CountdownTimer
                  key={`expiry-${timerKey}`}
                  timeInSeconds={CODE_TTL_SECONDS}
                  onTimerEnd={handleExpiry}
                />
              </>
            )}
          </span>
          <span css={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
              type="button"
              onClick={handleResend}
              disabled={resendDisabled || busy}
              css={linkBtnCss(resendDisabled || busy)}
            >
              {status === 'resending' ? 'Sending…' : 'Resend code'}
            </button>
            {resendDisabled && !busy && (
              <span
                css={{
                  ...(fonts.mini as object),
                  color: colors.darkerGrey,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <CountdownTimer
                  key={`cooldown-${timerKey}`}
                  timeInSeconds={RESEND_COOLDOWN_SECONDS}
                  onTimerEnd={handleCooldownEnd}
                />
                s
              </span>
            )}
          </span>
        </div>

        <button
          type="button"
          onClick={submitCode}
          disabled={!canSubmit}
          css={primaryBtnCss(!canSubmit)}
        >
          {status === 'verifying' ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'Verify'
          )}
        </button>

        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          css={linkBtnCss(busy)}
        >
          Back to sign in
        </button>
      </div>
    </div>
  );
}
